import React, { useState, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Plus,
  Trash2,
  Download,
  Package,
  AlertCircle,
  Check,
  ChevronsUpDown,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

interface BillOfMaterialsProps {
  form: UseFormReturn<any>;
  surveyId?: number;
}

interface BomItem {
  id?: number;
  surveyId?: number;
  category: string;
  itemName: string;
  description?: string;
  quantity: number;
  unit: string;
  supplierNotes?: string;
  notes?: string;
  isAutoPopulated: boolean;
}

interface BomSupplier {
  id: number;
  name: string;
  description?: string;
  website?: string;
  contactInfo?: string;
  usageCount: number;
  lastUsed: string;
  isActive: boolean;
}

interface BomCustomItem {
  id: number;
  itemName: string;
  category: string;
  unit: string;
  notes?: string;
  usageCount: number;
  lastUsed?: string;
  isActive: boolean;
}

const UNITS = ['pcs', 'meters', 'sets', 'rolls', 'boxes'];

const BOM_CATEGORIES = [
  { value: 'inverter', label: 'Inverter' },
  { value: 'battery', label: 'Battery' },
  { value: 'panel', label: 'Solar Panel' },
  { value: 'evCharger', label: 'EV Charger' },
  { value: 'gateway', label: 'Gateway' },
  { value: 'birdguard', label: 'Bird Guard' },
  { value: 'isolator', label: 'Isolator' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'cable', label: 'Cable' },
  { value: 'roofAccessories', label: 'Roof Accessories' },
  { value: 'scaffolding', label: 'Scaffolding' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'other', label: 'Other' },
];

// Display name mapping for legacy items - transforms technical names to user-friendly names
const DISPLAY_NAME_MAP: Record<string, string> = {
  // Distribution Boards
  '7 WAY BG DB': 'Indoor DB - Standard',
  '10 WAY DB WITH SPD': 'Indoor DB - Additional Ways',
  'IP65 MINI DB WITH SPD': 'External DB',
  
  // MCBs and RCBOs - format amperage for clarity
  '16A MCB': '16 Amps MCB',
  '20A MCB': '20 Amps MCB',
  '32A MCB': '32 Amps MCB',
  '40A MCB': '40 Amps MCB',
  '50A MCB': '50 Amps MCB',
  '63A MCB': '63 Amps MCB',
  '16A RCBO': '16 Amps RCBO',
  '20A RCBO': '20 Amps RCBO',
  '32A RCBO': '32 Amps RCBO',
  '40A RCBO': '40 Amps RCBO',
  '50A RCBO': '50 Amps RCBO',
  '63A RCBO': '63 Amps RCBO',
};

// Helper function to get user-friendly display name
const getDisplayName = (itemName: string): string => {
  return DISPLAY_NAME_MAP[itemName] || itemName;
};

// Common manual items that should always be pre-listed
const COMMON_MANUAL_ITEMS = [
  { itemName: 'DEKT Cable Entry', category: 'roofAccessories', unit: 'pcs', notes: 'LEAD' },
  { itemName: 'Tigo TAP', category: 'accessories', unit: 'pcs', notes: 'For enhanced monitoring' },
  { itemName: 'Tigo CCA', category: 'accessories', unit: 'pcs', notes: 'Cloud Connect Advanced' },
  { itemName: 'L + N TAIL BLOCKS PAIR', category: 'distribution', unit: 'sets', notes: '' },
  { itemName: 'SigenStor Installation Kit (Ground)', category: 'accessories', unit: 'sets', notes: '' },
  { itemName: 'Sigen Power Sensor Single Phase', category: 'accessories', unit: 'pcs', notes: '' },
  { itemName: 'Sigen Power Sensor Three Phase', category: 'accessories', unit: 'pcs', notes: '' },
];

// Common cable types for manual addition (reference list)
const COMMON_CABLE_TYPES = [
  { itemName: '6MM EV ULTRA', category: 'cable', unit: 'meters' },
  { itemName: '10MM EV ULTRA', category: 'cable', unit: 'meters' },
  { itemName: '6MM H07', category: 'cable', unit: 'meters' },
  { itemName: '10MM H07', category: 'cable', unit: 'meters' },
  { itemName: 'SWA Cable', category: 'cable', unit: 'meters' },
];

export function BillOfMaterials({ form, surveyId }: BillOfMaterialsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [enabledManualItems, setEnabledManualItems] = useState<Set<string>>(new Set());
  const hasAttemptedAutoSync = useRef(false);

  // Local state for notes to prevent lag during typing
  const [localNotes, setLocalNotes] = useState<Record<number, string>>({});

  // Custom item form state
  const [isCustomItemFormOpen, setIsCustomItemFormOpen] = useState(false);
  const [newCustomItem, setNewCustomItem] = useState({
    itemName: '',
    category: 'other',
    unit: 'pcs',
    notes: '',
  });

  // Fetch BOM items
  const { data: bomItems, isLoading: isLoadingItems } = useQuery({
    queryKey: ['bom-items', surveyId],
    queryFn: async () => {
      if (!surveyId) return [];
      console.log('[BOM Query] Fetching items for surveyId:', surveyId);
      const response = await api.get(`/api/bom/${surveyId}`);
      console.log('[BOM Query] Full response:', response);
      console.log('[BOM Query] response.data:', response.data);
      console.log('[BOM Query] Is array?', Array.isArray(response.data));
      if (response.data && response.data.length > 0) {
        console.log('[BOM Query] First item:', response.data[0]);
      }
      return response.data || [];
    },
    enabled: !!surveyId,
  });

  // Fetch BOM suppliers
  const { data: suppliers, isLoading: isLoadingSuppliers, error: suppliersError } = useQuery({
    queryKey: ['bom-suppliers'],
    queryFn: async () => {
      const response = await api.get('/api/bom-suppliers');
      console.log('[BOM Suppliers] API response:', response);

      // Defensive parsing - handle multiple response formats
      // Backend returns { success: true, data: [...] }
      // API client may return full response or just data
      if (response?.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      if (Array.isArray(response)) {
        return response;
      }

      console.warn('[BOM Suppliers] Unexpected response format:', response);
      return [];
    },
  });

  // Fetch custom BOM items
  const { data: customItems } = useQuery<BomCustomItem[]>({
    queryKey: ['bom-custom-items'],
    queryFn: async () => {
      const response = await api.get('/api/bom-custom-items');
      console.log('[BOM Custom Items] API response:', response);

      // Backend returns { success: true, data: [...] }
      // API client may return full response or unwrapped data
      if (response?.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }
      if (Array.isArray(response)) {
        return response;
      }

      console.warn('[BOM Custom Items] Unexpected response format:', response);
      return [];
    },
  });

  // Show error toast when supplier query fails
  useEffect(() => {
    if (suppliersError) {
      console.error('[BOM Suppliers] Failed to load suppliers:', suppliersError);
      toast({
        title: 'Failed to Load Suppliers',
        description: (suppliersError as any).response?.data?.message || (suppliersError as any).message || 'Could not fetch supplier list',
        variant: 'destructive',
      });
    }
  }, [suppliersError, toast]);

  // Auto-sync on mount
  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!surveyId) throw new Error('Survey ID is required');

      // Sync BOM items from database (auto-save happens separately every 30s)
      console.log('[BOM] Syncing equipment from database...');
      const response = await api.post(`/api/bom/${surveyId}/sync`);
      console.log('[BOM] Sync response:', response);
      // Return full response to access diagnostics in onSuccess
      return response;
    },
    onSuccess: (data) => {
      console.log('[BOM] Sync successful, items created:', data.data?.length || 0);
      console.log('[BOM] Diagnostics:', data.diagnostics);
      queryClient.invalidateQueries({ queryKey: ['bom-items', surveyId] });

      const itemCount = data.diagnostics?.itemsCreated || 0;

      if (itemCount === 0) {
        // No items were created - survey is likely empty
        const fieldsFound = data.diagnostics?.surveyFieldsFound;
        const emptyFields = [];
        if (!fieldsFound?.panels) emptyFields.push('Panels');
        if (!fieldsFound?.inverters || fieldsFound.inverters === 0) emptyFields.push('Inverters');
        if (!fieldsFound?.battery) emptyFields.push('Battery');
        if (!fieldsFound?.evCharger) emptyFields.push('EV Charger');

        toast({
          title: 'No Equipment Found',
          description: emptyFields.length > 0
            ? `Missing equipment data. Please fill out: ${emptyFields.join(', ')} in the Project Info section, then click "Sync from Survey" again.`
            : 'No equipment data found. Please fill out equipment sections in the survey, then click "Sync from Survey" again.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Synced Successfully',
          description: `${itemCount} equipment item${itemCount > 1 ? 's' : ''} synced to BOM`,
        });
      }
    },
    onError: (error: any) => {
      console.error('[BOM] Auto-sync failed:', error);
      toast({
        title: 'Sync Failed',
        description: error.response?.data?.message || 'Failed to sync items from survey',
        variant: 'destructive',
      });
    },
  });

  // Update BOM item (with optimistic updates)
  const updateItemMutation = useMutation({
    mutationFn: async ({ itemId, updates }: { itemId: number; updates: Partial<BomItem> }) => {
      const response = await api.put(`/api/bom/items/${itemId}`, updates);
      return response.data;
    },
    onMutate: async ({ itemId, updates }) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['bom-items', surveyId] });

      // Snapshot current data for rollback
      const previousBom = queryClient.getQueryData(['bom-items', surveyId]);

      // Optimistically update cache
      queryClient.setQueryData(['bom-items', surveyId], (old: any) => {
        if (!old) return old;
        return old.map((item: any) =>
          item.id === itemId ? { ...item, ...updates } : item
        );
      });

      return { previousBom };
    },
    onError: (error: any, variables, context) => {
      // Rollback on error
      if (context?.previousBom) {
        queryClient.setQueryData(['bom-items', surveyId], context.previousBom);
      }
      toast({
        title: 'Update Failed',
        description: error.response?.data?.message || 'Changes have been reverted',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      // Optionally refetch to ensure sync with server (disabled for performance)
      // queryClient.invalidateQueries({ queryKey: ['bom-items', surveyId] });
    },
  });

  // Add BOM item (with optimistic updates)
  const addItemMutation = useMutation({
    mutationFn: async (item: Partial<BomItem>) => {
      if (!surveyId) throw new Error('Survey ID is required');
      const response = await api.post(`/api/bom/${surveyId}`, item);
      return response.data;
    },
    onMutate: async (newItem) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['bom-items', surveyId] });

      // Snapshot current data for rollback
      const previousBom = queryClient.getQueryData(['bom-items', surveyId]);

      // Optimistically add item with temporary ID
      const optimisticItem = {
        ...newItem,
        id: Date.now(), // Temporary ID
        surveyId,
      };

      queryClient.setQueryData(['bom-items', surveyId], (old: any) => {
        if (!old) return [optimisticItem];
        return [...old, optimisticItem];
      });

      return { previousBom };
    },
    onError: (error: any, variables, context) => {
      // Rollback on error
      if (context?.previousBom) {
        queryClient.setQueryData(['bom-items', surveyId], context.previousBom);
      }
      toast({
        title: 'Add Failed',
        description: error.response?.data?.message || 'Failed to add item',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      // Refetch to get real ID from server
      queryClient.invalidateQueries({ queryKey: ['bom-items', surveyId] });
      toast({
        title: 'Item Added',
        description: 'Item has been added successfully',
      });
    },
  });

  // Delete BOM item (with optimistic updates)
  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: number) => {
      await api.delete(`/api/bom/items/${itemId}`);
    },
    onMutate: async (itemId) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['bom-items', surveyId] });

      // Snapshot current data for rollback
      const previousBom = queryClient.getQueryData(['bom-items', surveyId]);

      // Optimistically remove item
      queryClient.setQueryData(['bom-items', surveyId], (old: any) => {
        if (!old) return old;
        return old.filter((item: any) => item.id !== itemId);
      });

      return { previousBom };
    },
    onError: (error: any, variables, context) => {
      // Rollback on error
      if (context?.previousBom) {
        queryClient.setQueryData(['bom-items', surveyId], context.previousBom);
      }
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.message || 'Item could not be deleted',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Item Deleted',
        description: 'Item has been removed',
      });
    },
  });

  // Increment custom item usage count
  const incrementCustomItemUsageMutation = useMutation({
    mutationFn: async (customItemId: number) => {
      await api.post(`/api/bom-custom-items/${customItemId}/use`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bom-custom-items'] });
    },
  });

  // Create custom item
  const createCustomItemMutation = useMutation({
    mutationFn: async (item: { itemName: string; category: string; unit: string; notes?: string }) => {
      const response = await api.post('/api/bom-custom-items', item);
      // Backend returns { success: true, data: {...} }
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bom-custom-items'] });
      toast({
        title: 'Custom Item Created',
        description: 'The item has been saved and is now available for all surveys',
      });
      setIsCustomItemFormOpen(false);
      setNewCustomItem({ itemName: '', category: 'other', unit: 'pcs', notes: '' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Create Item',
        description: error.response?.data?.message || 'Could not create custom item',
        variant: 'destructive',
      });
    },
  });

  // Create supplier
  const createSupplierMutation = useMutation({
    mutationFn: async (supplierName: string) => {
      console.log('[BOM Suppliers] Creating supplier:', supplierName);
      const response = await api.post('/api/bom-suppliers', { name: supplierName });
      console.log('[BOM Suppliers] Create response:', response);

      // Defensive parsing - handle both { data: {...} } and direct object
      return response?.data || response;
    },
    onSuccess: (data) => {
      console.log('[BOM Suppliers] Created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['bom-suppliers'] });
    },
    onError: (error: any) => {
      console.error('[BOM Suppliers] Failed to create supplier:', error);
      toast({
        title: 'Failed to Create Supplier',
        description: error.response?.data?.message || error.message || 'Could not add supplier to database',
        variant: 'destructive',
      });
    },
  });

  // Auto-trigger sync when tab opens (if no items exist)
  useEffect(() => {
    console.log('[BOM] useEffect - surveyId:', surveyId, 'bomItems:', bomItems?.length, 'hasAttempted:', hasAttemptedAutoSync.current, 'isPending:', syncMutation.isPending);

    // Only auto-sync once when component mounts with empty items
    if (surveyId && bomItems !== undefined && bomItems.length === 0 && !hasAttemptedAutoSync.current && !syncMutation.isPending) {
      console.log('[BOM] Triggering auto-sync...');
      hasAttemptedAutoSync.current = true;
      syncMutation.mutate();
    }
  }, [surveyId, bomItems, syncMutation]);

  // Separate auto-populated and manual items
  const autoPopulatedItems = bomItems?.filter((item: BomItem) => item.isAutoPopulated) || [];
  const existingManualItems = bomItems?.filter((item: BomItem) => !item.isAutoPopulated) || [];

  console.log('[BOM] Render - Total items:', bomItems?.length, 'Auto:', autoPopulatedItems.length, 'Manual:', existingManualItems.length);

  // Combine COMMON_MANUAL_ITEMS with custom items
  const allManualItemTemplates = [
    ...COMMON_MANUAL_ITEMS,
    ...(customItems || []).map((customItem) => ({
      itemName: customItem.itemName,
      category: customItem.category,
      unit: customItem.unit,
      notes: customItem.notes,
      customItemId: customItem.id, // Track the custom item ID for usage increment
      usageCount: customItem.usageCount, // Track usage for sorting
    })),
  ];

  // Sort by usage count (if present) so frequently used items appear first
  const sortedManualItemTemplates = allManualItemTemplates.sort((a, b) => {
    const aUsage = 'usageCount' in a ? (a.usageCount || 0) : 0;
    const bUsage = 'usageCount' in b ? (b.usageCount || 0) : 0;
    return bUsage - aUsage;
  });

  // Filter out items that already exist in the survey
  const existingManualItemNames = new Set(
    existingManualItems.map((item: BomItem) => item.itemName.toLowerCase())
  );
  const availableManualItems = sortedManualItemTemplates.filter(
    (item) => !existingManualItemNames.has(item.itemName.toLowerCase())
  );

  const handleQuantityChange = (itemId: number, quantity: number) => {
    if (quantity < 0) return;
    updateItemMutation.mutate({ itemId, updates: { quantity } });
  };

  const handleNotesChange = (itemId: number, notes: string) => {
    updateItemMutation.mutate({ itemId, updates: { notes } });
  };

  const handleSupplierChange = async (itemId: number, supplierName: string) => {
    // Create supplier if it doesn't exist
    const supplierExists = suppliers?.some(
      (s: BomSupplier) => s.name.toLowerCase() === supplierName.toLowerCase()
    );
    if (!supplierExists && supplierName.trim()) {
      await createSupplierMutation.mutateAsync(supplierName.trim());
    }

    updateItemMutation.mutate({ itemId, updates: { supplierNotes: supplierName } });
  };

  const handleEnableManualItem = (itemName: string, enabled: boolean) => {
    if (enabled) {
      // Find the item from all manual item templates
      const manualItem = allManualItemTemplates.find((item) => item.itemName === itemName);
      if (manualItem) {
        // Add the item to BOM
        addItemMutation.mutate({
          category: manualItem.category,
          itemName: manualItem.itemName,
          unit: manualItem.unit,
          notes: manualItem.notes,
          quantity: 1,
          isAutoPopulated: false,
        });

        // If this is a custom item, increment its usage count
        if ('customItemId' in manualItem && manualItem.customItemId) {
          incrementCustomItemUsageMutation.mutate(manualItem.customItemId);
        }
      }
      setEnabledManualItems((prev) => new Set([...prev, itemName]));
    } else {
      // Remove the item
      const itemToDelete = existingManualItems.find((item: BomItem) => item.itemName === itemName);
      if (itemToDelete?.id) {
        deleteItemMutation.mutate(itemToDelete.id);
      }
      setEnabledManualItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemName);
        return newSet;
      });
    }
  };

  const handleManualSync = () => {
    if (!surveyId || syncMutation.isPending) return;
    console.log('[BOM] Manual sync triggered');
    hasAttemptedAutoSync.current = true; // Mark as attempted
    syncMutation.mutate();
  };

  const handleDownloadPdf = async () => {
    if (!surveyId) return;

    // Check if there are items before attempting download
    if (!bomItems || bomItems.length === 0) {
      toast({
        title: 'No Items Found',
        description: 'Please sync equipment from survey first',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get token for authorization - API client handles this automatically
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`/api/bom/${surveyId}/pdf`, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        // Handle authentication errors from backend
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication required. Please log in and try again.');
        }
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BOM-Survey-${surveyId}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'PDF Downloaded',
        description: 'BOM PDF has been downloaded successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Failed to download BOM PDF',
        variant: 'destructive',
      });
    }
  };

  if (!surveyId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Bill of Materials
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Please save the survey first to access the Bill of Materials
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoadingItems || syncMutation.isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Bill of Materials
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">
              {syncMutation.isPending ? 'Syncing equipment from survey...' : 'Loading equipment list...'}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Bill of Materials
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsCustomItemFormOpen(!isCustomItemFormOpen)}
                data-testid="button-add-custom-item"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Item
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualSync}
                disabled={syncMutation.isPending}
                data-testid="button-sync-from-survey"
              >
                {syncMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync from Survey
                  </>
                )}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={!bomItems || bomItems.length === 0}
                data-testid="button-download-pdf"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Inline Custom Item Form */}
            {isCustomItemFormOpen && (
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold mb-1">Create Custom BOM Item</h3>
                    <p className="text-xs text-muted-foreground">
                      Add a new item that will be available across all site surveys for quick selection.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-name">Item Name*</Label>
                      <Input
                        id="item-name"
                        placeholder="e.g., Custom Cable Kit"
                        value={newCustomItem.itemName}
                        onChange={(e) => setNewCustomItem({ ...newCustomItem, itemName: e.target.value })}
                        data-testid="input-custom-item-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category*</Label>
                      <Select
                        value={newCustomItem.category}
                        onValueChange={(value) => setNewCustomItem({ ...newCustomItem, category: value })}
                      >
                        <SelectTrigger id="category" data-testid="select-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BOM_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit">Unit*</Label>
                      <Select
                        value={newCustomItem.unit}
                        onValueChange={(value) => setNewCustomItem({ ...newCustomItem, unit: value })}
                      >
                        <SelectTrigger id="unit" data-testid="select-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Input
                        id="notes"
                        placeholder="e.g., For special installations"
                        value={newCustomItem.notes}
                        onChange={(e) => setNewCustomItem({ ...newCustomItem, notes: e.target.value })}
                        data-testid="input-notes"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsCustomItemFormOpen(false);
                        setNewCustomItem({ itemName: '', category: 'other', unit: 'pcs', notes: '' });
                      }}
                      data-testid="button-cancel-custom-item"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => createCustomItemMutation.mutate(newCustomItem)}
                      disabled={!newCustomItem.itemName || createCustomItemMutation.isPending}
                      data-testid="button-save-custom-item"
                    >
                      {createCustomItemMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Item'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Auto-detected Equipment */}
            {autoPopulatedItems.length > 0 && (
              <div>
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-foreground">
                    Auto-Detected Equipment
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Populated from survey data - edit quantities and suppliers as needed
                  </p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Item</TableHead>
                      <TableHead className="w-[12%]">Quantity</TableHead>
                      <TableHead className="w-[10%]">Unit</TableHead>
                      <TableHead className="w-[25%]">Supplier</TableHead>
                      <TableHead className="w-[20%]">Notes</TableHead>
                      <TableHead className="w-[3%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {autoPopulatedItems.map((item: BomItem) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-sm">{getDisplayName(item.itemName)}</div>
                            {item.description && (
                              <div className="text-xs text-muted-foreground">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            value={item.quantity}
                            onChange={(e) =>
                              item.id && handleQuantityChange(item.id, parseInt(e.target.value) || 0)
                            }
                            className="h-8 w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{item.unit}</span>
                        </TableCell>
                        <TableCell>
                          <SupplierSelector
                            item={item}
                            suppliers={suppliers}
                            isLoadingSuppliers={isLoadingSuppliers}
                            suppliersError={suppliersError}
                            onSupplierChange={handleSupplierChange}
                          />
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={item.id && localNotes[item.id] !== undefined ? localNotes[item.id] : (item.notes || '')}
                            onChange={(e) => {
                              if (item.id) {
                                setLocalNotes(prev => ({ ...prev, [item.id!]: e.target.value }));
                              }
                            }}
                            onBlur={(e) => {
                              if (item.id) {
                                handleNotesChange(item.id, e.target.value);
                              }
                            }}
                            placeholder="Add notes..."
                            className="min-h-[60px] text-xs resize-none"
                            rows={2}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => item.id && deleteItemMutation.mutate(item.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Existing Manual Items */}
            {existingManualItems.length > 0 && (
              <div>
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Additional Materials</h3>
                  <p className="text-xs text-muted-foreground">
                    Items you've added manually
                  </p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Item</TableHead>
                      <TableHead className="w-[12%]">Quantity</TableHead>
                      <TableHead className="w-[10%]">Unit</TableHead>
                      <TableHead className="w-[25%]">Supplier</TableHead>
                      <TableHead className="w-[20%]">Notes</TableHead>
                      <TableHead className="w-[3%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {existingManualItems.map((item: BomItem) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-medium text-sm">{getDisplayName(item.itemName)}</div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            value={item.quantity}
                            onChange={(e) =>
                              item.id && handleQuantityChange(item.id, parseInt(e.target.value) || 0)
                            }
                            className="h-8 w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{item.unit}</span>
                        </TableCell>
                        <TableCell>
                          <SupplierSelector
                            item={item}
                            suppliers={suppliers}
                            isLoadingSuppliers={isLoadingSuppliers}
                            suppliersError={suppliersError}
                            onSupplierChange={handleSupplierChange}
                          />
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={item.id && localNotes[item.id] !== undefined ? localNotes[item.id] : (item.notes || '')}
                            onChange={(e) => {
                              if (item.id) {
                                setLocalNotes(prev => ({ ...prev, [item.id!]: e.target.value }));
                              }
                            }}
                            onBlur={(e) => {
                              if (item.id) {
                                handleNotesChange(item.id, e.target.value);
                              }
                            }}
                            placeholder="Add notes..."
                            className="min-h-[60px] text-xs resize-none"
                            rows={2}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => item.id && deleteItemMutation.mutate(item.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Common Manual Items (Pre-listed) */}
            {availableManualItems.length > 0 && (
              <div>
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-foreground">
                    Common Additional Items
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Check to add these commonly needed items to your BOM
                  </p>
                </div>
                <div className="space-y-2">
                  {availableManualItems.map((item) => {
                    const isCustomItem = 'customItemId' in item;
                    const usageCount = 'usageCount' in item ? item.usageCount : 0;
                    
                    return (
                      <div
                        key={item.itemName}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                      >
                        <Checkbox
                          id={item.itemName}
                          checked={enabledManualItems.has(item.itemName)}
                          onCheckedChange={(checked) =>
                            handleEnableManualItem(item.itemName, checked as boolean)
                          }
                        />
                        <label
                          htmlFor={item.itemName}
                          className="flex-1 text-sm font-medium cursor-pointer"
                        >
                          {item.itemName}
                          {item.notes && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({item.notes})
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-2">
                          {isCustomItem && usageCount > 0 && (
                            <Badge variant="outline" className="text-xs">
                              Used {usageCount}×
                            </Badge>
                          )}
                          <Badge variant="secondary" className="text-xs">
                            {item.unit}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// SupplierSelector component - defined outside BillOfMaterials to prevent re-mounting
interface SupplierSelectorProps {
  item: BomItem;
  suppliers: BomSupplier[] | undefined;
  isLoadingSuppliers: boolean;
  suppliersError: unknown;
  onSupplierChange: (itemId: number, supplierName: string) => Promise<void>;
}

const SupplierSelector = ({
  item,
  suppliers,
  isLoadingSuppliers,
  suppliersError,
  onSupplierChange,
}: SupplierSelectorProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleCreateSupplier = async (name: string) => {
    if (!item.id) {
      toast({
        title: 'Error',
        description: 'Item ID is missing. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    try {
      console.log('[BOM Suppliers] handleCreateSupplier called with:', name);
      await onSupplierChange(item.id, name);
      toast({
        title: 'Supplier added',
        description: `"${name}" has been added to your suppliers list`,
      });
      setSearchValue('');
      setOpen(false);
    } catch (error: any) {
      console.error('[BOM Suppliers] handleCreateSupplier error:', error);
      toast({
        title: 'Failed to add supplier',
        description: error.message || 'An error occurred while adding the supplier',
        variant: 'destructive',
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full justify-between text-sm font-normal"
          disabled={isLoadingSuppliers}
        >
          <span className="truncate">
            {suppliersError
              ? 'Error loading suppliers - click to add manually'
              : isLoadingSuppliers
              ? 'Loading suppliers...'
              : item.supplierNotes || 'Select supplier...'}
          </span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search suppliers or add new..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>
              {isLoadingSuppliers ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading suppliers...
                </div>
              ) : searchValue ? (
                <div className="p-3 space-y-2">
                  {!!suppliersError && (
                    <div className="text-xs text-destructive text-center mb-2">
                      Error loading suppliers list
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground text-center mb-2">
                    {suppliersError ? 'Add supplier manually' : 'Supplier not found in list'}
                  </div>
                  <Button
                    size="sm"
                    variant="default"
                    className="w-full bg-primary hover:bg-primary/90"
                    onClick={() => handleCreateSupplier(searchValue)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add "{searchValue}" as New Supplier
                  </Button>
                </div>
              ) : (
                <div className="py-6 text-center space-y-2">
                  {!!suppliersError && (
                    <div className="text-sm text-destructive mb-2">
                      Error loading suppliers
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground">
                    {suppliersError ? 'You can still add suppliers manually' : 'No suppliers yet'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Type a supplier name above to add one
                  </div>
                </div>
              )}
            </CommandEmpty>
            <CommandGroup heading={suppliers && suppliers.length > 0 ? "Existing Suppliers" : undefined}>
              {suppliers?.map((supplier: BomSupplier) => (
                <CommandItem
                  key={supplier.id}
                  value={supplier.name}
                  onSelect={() => {
                    if (item.id) {
                      onSupplierChange(item.id, supplier.name);
                    }
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      item.supplierNotes === supplier.name ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center justify-between flex-1">
                    <span>{supplier.name}</span>
                    <Badge variant="secondary" className="ml-2 text-xs">
                      ×{supplier.usageCount}
                    </Badge>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {suppliers && suppliers.length > 0 && (
              <CommandGroup>
                <div className="p-2 border-t">
                  <div className="text-xs text-muted-foreground mb-2 px-2">
                    Can't find your supplier? Type a new name above
                  </div>
                </div>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
